<template>
  <div id="app">
    <router-view v-if="route.path === '/login' || route.path.startsWith('/editor')" />
    <el-container v-else class="h-screen bg-slate-50">
      <el-header class="bg-white border-b border-slate-200 flex items-center justify-between px-6 h-16">
        <div class="flex items-center gap-4">
          <div class="flex items-center cursor-pointer" @click="router.push('/')">
            <img src="/laper-ai-logo-no-slogan.png" alt="Laper AI Logo" class="h-8 w-auto transition-transform hover:scale-105" />
          </div>
          <div v-if="displayedTitle" class="hidden max-w-[96px] truncate text-sm text-slate-500 lg:block">{{ displayedTitle }}</div>
          <el-divider direction="vertical" />
          <el-button text @click="router.push('/')">工作台</el-button>
          <el-button text @click="router.push('/upload')">新建工作流</el-button>
          <el-badge :value="undownloadedExportCount" :hidden="undownloadedExportCount === 0" class="inline-flex">
            <el-button text @click="openExportRecords">导出记录</el-button>
          </el-badge>
          <el-divider direction="vertical" class="!mx-1" />
          <el-button text @click="router.push('/usage')">我的额度</el-button>
          <el-badge :value="pendingShareCount" :hidden="pendingShareCount === 0" class="inline-flex">
            <el-button text @click="router.push('/shares')">分享中心</el-button>
          </el-badge>
          <el-button v-if="isAdmin" text @click="router.push('/users')">用户管理</el-button>
          <el-button v-if="isAdmin" text @click="router.push('/admin/usage')">点数管理</el-button>
          <el-button text @click="router.push('/help')">帮助中心</el-button>
        </div>

        <div class="flex items-center gap-3">
          <el-tag
            v-if="currentUser && usageBalance !== null"
            size="small"
            type="warning"
            effect="plain"
            class="cursor-pointer"
            @click="router.push('/usage')"
          >
            AI 点数：{{ usageBalance }}
          </el-tag>
          <el-tag v-if="currentUser" size="small" :type="isAdmin ? 'danger' : 'info'" effect="plain">
            {{ currentUser?.role }}
          </el-tag>
          <el-dropdown v-if="currentUser" trigger="click">
            <span class="flex items-center gap-2 cursor-pointer select-none">
              <el-icon><UserFilled /></el-icon>
              <span class="text-sm font-medium text-slate-800">{{ currentUser?.username }}</span>
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button v-else size="small" @click="handleLogout">退出登录</el-button>
        </div>
      </el-header>

      <el-main class="p-6 overflow-auto">
        <router-view />
      </el-main>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowDown, UserFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { apiClient } from '@/services/api/client'
import type { GenerationTask } from '@/types/api'

const route = useRoute()
const router = useRouter()

type CurrentUser = { id: string; username: string; role: string }
const currentUser = ref<CurrentUser | null>(null)
const usageBalance = ref<number | null>(null)
const AUTH_USER_KEY = 'ppt_agent_user'
const pageTitle = computed(() => {
  if (route.path === '/') {
    return ''
  }
  const titles: Record<string, string> = {
    '/upload': '上传文档',
    '/outline': '大纲管理',
    '/preview': '预览',
    '/export': '导出记录',
    '/help': '帮助中心',
    '/usage': '我的额度',
        '/shares': '分享中心',
    '/users': '用户管理',
    '/admin/usage': '点数管理',
  }
  return titles[route.path] || ''
})

const displayedTitle = computed(() => {
  return pageTitle.value
})

const isAdmin = computed(() => String(currentUser.value?.role || '').toLowerCase() === 'admin')

const EXPORT_DOWNLOADED_KEY = 'ppt_agent_export_downloaded_task_ids'
const EXPORT_NOTIFIED_KEY = 'ppt_agent_export_notified_task_ids'
const EXPORT_STATUS_EVENT = 'ppt-agent-export-status-changed'
const SHARE_NOTIFIED_KEY = 'ppt_agent_notified_pending_share_ids'

const exportTasks = ref<GenerationTask[]>([])
const undownloadedExportCount = ref(0)
const pendingShareCount = ref(0)
let exportPollingTimer: number | null = null
let sharePollingTimer: number | null = null
const isRefreshingExportTasks = ref(false)
const isRefreshingPendingShares = ref(false)

const safeParseStringArray = (raw: string | null): string[] => {
  if (!raw) return []
  try {
    const val = JSON.parse(raw) as unknown
    if (!Array.isArray(val)) return []
    return val.map((item) => String(item)).filter(Boolean)
  } catch {
    return []
  }
}

const readTaskIdSet = (key: string): Set<string> => {
  return new Set(safeParseStringArray(window.localStorage.getItem(key)))
}

const writeTaskIdSet = (key: string, set: Set<string>) => {
  const list = Array.from(set).slice(-300)
  window.localStorage.setItem(key, JSON.stringify(list))
}

const getTaskRequestData = (task: GenerationTask): Record<string, unknown> => {
  const requestData = task.request_data
  return requestData && typeof requestData === 'object' ? requestData : {}
}

const getTaskResultData = (task: GenerationTask): Record<string, unknown> => {
  const resultData = task.result_data
  return resultData && typeof resultData === 'object' ? resultData : {}
}

const taskFileUrl = (task: GenerationTask) => {
  const resultData = getTaskResultData(task)
  return typeof resultData.file_url === 'string' ? resultData.file_url : ''
}

const taskDocumentTitle = (task: GenerationTask) => {
  const requestData = getTaskRequestData(task)
  const resultData = getTaskResultData(task)
  return String(resultData.document_title || requestData.document_title || '未命名文档')
}

const markExportRecordsViewed = (tasks: GenerationTask[]) => {
  const downloaded = readTaskIdSet(EXPORT_DOWNLOADED_KEY)
  const readyTaskIds = tasks
    .filter((task) => String(task.status || '').toLowerCase() === 'completed')
    .filter((task) => !!taskFileUrl(task))
    .map((task) => String(task.task_id || ''))
    .filter(Boolean)

  let changed = false
  readyTaskIds.forEach((taskId) => {
    if (!downloaded.has(taskId)) {
      downloaded.add(taskId)
      changed = true
    }
  })

  if (changed) {
    writeTaskIdSet(EXPORT_DOWNLOADED_KEY, downloaded)
  }
  undownloadedExportCount.value = 0
}

const updateExportIndicators = (tasks: GenerationTask[]) => {
  const downloaded = readTaskIdSet(EXPORT_DOWNLOADED_KEY)
  const notified = readTaskIdSet(EXPORT_NOTIFIED_KEY)

  const readyTasks = tasks
    .filter((task) => String(task.status || '').toLowerCase() === 'completed')
    .filter((task) => !!taskFileUrl(task))

  const undownloaded = readyTasks.filter((task) => !downloaded.has(String(task.task_id || '')))
  undownloadedExportCount.value = undownloaded.length

  const newlyReady = undownloaded
    .filter((task) => !notified.has(String(task.task_id || '')))
    .slice(0, 5)

  if (newlyReady.length === 1) {
    const title = taskDocumentTitle(newlyReady[0])
    ElMessage.success(`《${title}》导出已生成，可在“导出记录”下载`)
  } else if (newlyReady.length > 1) {
    ElMessage.success(`${newlyReady.length} 个导出已生成，可在“导出记录”下载`)
  }

  if (newlyReady.length > 0) {
    newlyReady.forEach((task) => notified.add(String(task.task_id || '')))
    writeTaskIdSet(EXPORT_NOTIFIED_KEY, notified)
  }
}

const refreshExportTasks = async () => {
  if (!currentUser.value) return
  if (route.path === '/login' || route.path.startsWith('/editor')) return
  if (route.path === '/export') return
  if (isRefreshingExportTasks.value) return
  isRefreshingExportTasks.value = true
  try {
    exportTasks.value = await apiClient.getTaskHistory({ limit: 50, taskType: 'export' })
    updateExportIndicators(exportTasks.value)
  } catch {
    undownloadedExportCount.value = 0
  } finally {
    isRefreshingExportTasks.value = false
  }
}

const refreshPendingShares = async () => {
  if (!currentUser.value) return
  if (route.path === '/login' || route.path.startsWith('/editor')) return
  if (isRefreshingPendingShares.value) return

  isRefreshingPendingShares.value = true
  try {
    const result = await apiClient.getIncomingWorkflowShares('pending')
    const items = result.items || []
    pendingShareCount.value = items.length

    const notified = readTaskIdSet(SHARE_NOTIFIED_KEY)
    const newlyPending = items.filter((item) => !notified.has(String(item.id || '')))
    const shouldToast = route.path !== '/shares'

    if (shouldToast && newlyPending.length === 1) {
      const share = newlyPending[0]
      ElMessage.warning(`收到新的工作流分享：《${share.document_title || '未命名工作流'}》，请前往“分享中心”确认`)
    } else if (shouldToast && newlyPending.length > 1) {
      ElMessage.warning(`收到 ${newlyPending.length} 个新的工作流分享，请前往“分享中心”确认`)
    }

    if (newlyPending.length > 0) {
      newlyPending.forEach((item) => notified.add(String(item.id || '')))
      writeTaskIdSet(SHARE_NOTIFIED_KEY, notified)
    }
  } catch {
    pendingShareCount.value = 0
  } finally {
    isRefreshingPendingShares.value = false
  }
}

const hasActiveExportTask = () => {
  return exportTasks.value.some((task) => {
    const status = String(task.status || '').toLowerCase()
    return status === 'pending' || status === 'processing'
  })
}

const getExportPollInterval = () => {
  if (route.path === '/export') return 0
  const hidden = typeof document !== 'undefined' ? document.hidden : false
  const hasActive = hasActiveExportTask()
  if (hidden) {
    return hasActive ? 10000 : 30000
  }
  return hasActive ? 3000 : 10000
}

const getSharePollInterval = () => {
  const hidden = typeof document !== 'undefined' ? document.hidden : false
  return hidden ? 30000 : 15000
}

const scheduleNextExportPolling = (delay?: number) => {
  stopExportPolling()
  const nextDelay = typeof delay === 'number' ? delay : getExportPollInterval()
  if (nextDelay <= 0) return
  exportPollingTimer = window.setTimeout(async () => {
    await refreshExportTasks()
    scheduleNextExportPolling()
  }, nextDelay)
}

const startExportPolling = () => {
  if (route.path === '/export') {
    stopExportPolling()
    return
  }
  if (exportPollingTimer !== null) return
  scheduleNextExportPolling(0)
}

const stopExportPolling = () => {
  if (exportPollingTimer === null) return
  window.clearTimeout(exportPollingTimer)
  exportPollingTimer = null
}

const scheduleNextSharePolling = (delay?: number) => {
  stopSharePolling()
  const nextDelay = typeof delay === 'number' ? delay : getSharePollInterval()
  if (nextDelay <= 0) return
  sharePollingTimer = window.setTimeout(async () => {
    await refreshPendingShares()
    scheduleNextSharePolling()
  }, nextDelay)
}

const startSharePolling = () => {
  if (route.path === '/login' || route.path.startsWith('/editor')) {
    stopSharePolling()
    return
  }
  if (sharePollingTimer !== null) return
  scheduleNextSharePolling(0)
}

const stopSharePolling = () => {
  if (sharePollingTimer === null) return
  window.clearTimeout(sharePollingTimer)
  sharePollingTimer = null
}

const loadStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(AUTH_USER_KEY)
    if (!raw) {
      currentUser.value = null
      return
    }
    currentUser.value = JSON.parse(raw) as CurrentUser
  } catch {
    currentUser.value = null
  }
}

const refreshMe = async () => {
  try {
    const me = await apiClient.getMe()
    currentUser.value = me
  } catch {
    loadStoredUser()
  }
}

const refreshUsageBalance = async () => {
  if (!currentUser.value) {
    usageBalance.value = null
    return
  }
  try {
    const summary = await apiClient.getMyUsageSummary()
    usageBalance.value = Number(summary.balance_points ?? 0)
  } catch {
    usageBalance.value = null
  }
}

const handleLogout = async () => {
  await apiClient.logout()
  usageBalance.value = null
  await router.replace('/login')
}

const openExportRecords = async () => {
  if (route.path === '/export') {
    return
  }
  undownloadedExportCount.value = 0
  await router.push({
    name: 'export',
    query: { from: route.fullPath },
  })
}

const handleExportStatusChanged = () => {
  if (route.path === '/export') {
    markExportRecordsViewed(exportTasks.value)
    return
  }
  updateExportIndicators(exportTasks.value)
  scheduleNextExportPolling()
}

const handleVisibilityChange = () => {
  if (route.path === '/login' || route.path.startsWith('/editor')) return
  if (route.path === '/export') return
  scheduleNextExportPolling(document.hidden ? 1000 : 0)
  scheduleNextSharePolling(document.hidden ? 1000 : 0)
}

onMounted(async () => {
  loadStoredUser()
  await refreshMe()
  await refreshUsageBalance()
  await refreshExportTasks()
  await refreshPendingShares()
  startExportPolling()
  startSharePolling()
  window.addEventListener(EXPORT_STATUS_EVENT, handleExportStatusChanged)
  window.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  stopExportPolling()
  stopSharePolling()
  window.removeEventListener(EXPORT_STATUS_EVENT, handleExportStatusChanged)
  window.removeEventListener('visibilitychange', handleVisibilityChange)
})

watch(
  () => route.fullPath,
  async () => {
    if (route.path === '/login' || route.path.startsWith('/editor')) {
      stopExportPolling()
      stopSharePolling()
      return
    }
    if (route.path === '/export') {
      stopExportPolling()
      markExportRecordsViewed(exportTasks.value)
      return
    }
    if (!currentUser.value) {
      loadStoredUser()
    }
    if (!currentUser.value) {
      await refreshMe()
    }
    if (currentUser.value) {
      await refreshUsageBalance()
      startExportPolling()
      startSharePolling()
      await refreshExportTasks()
      await refreshPendingShares()
    }
  },
)
</script>

<style scoped>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
